// NotificationContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import ApiService, {
  initialAuthState
} from '../services/ApiService';

import socket from '../socket';

const NotificationContext = createContext({

  notifications: [],
  generalNotifications: [],
  orderNotifications: [],

  counts: {
    generalUnreadCount: 0,
    orderUnreadCount: 0,
    totalUnreadCount: 0
  },

  loading: false,

  refreshNotifications: () => { },

  markAsRead: () => { },

  markAllAsRead: () => { }

});

export const useNotificationContext = () =>
  useContext(NotificationContext);

export const NotificationProvider = ({
  children
}) => {

  const [notifications, setNotifications] =
    useState([]);

  const [
    generalNotifications,
    setGeneralNotifications
  ] = useState([]);

  const [
    orderNotifications,
    setOrderNotifications
  ] = useState([]);

  const [counts, setCounts] = useState({

    generalUnreadCount: 0,
    orderUnreadCount: 0,
    totalUnreadCount: 0

  });

  const [loading, setLoading] =
    useState(false);

  const audioRef = useRef(null);

  const notifyStaffId =
    localStorage.getItem("id");

  const role =
    localStorage.getItem("role");

  const isAdmin =
    role === 'admin' ||
    role === 'CEO';

  // ✅ PRELOAD AUDIO

  useEffect(() => {

    audioRef.current =
      new Audio("/sounds/notification1.mp3");

  }, []);

  // ✅ FETCH NOTIFICATIONS

  const fetchNotifications =
    async (selectedBranch = 'All') => {

      setLoading(true);

      try {

        const payload = {

          companyCode:
            initialAuthState.companyCode,

          unitCode:
            initialAuthState.unitCode,

          notifyStaffId

        };

        if (selectedBranch !== 'All') {

          payload.branchName =
            selectedBranch;

        }

        const apiUrl = isAdmin
          ? '/notifications/getAllAdminNotifications'
          : '/notifications/getAllNotifications';

        const res =
          await ApiService.post(
            apiUrl,
            payload
          );

        if (res.status) {

          setNotifications(
            res.data.notifications || []
          );

          setGeneralNotifications(
            res.data.generalNotifications || []
          );

          setOrderNotifications(
            res.data.orderNotifications || []
          );

          setCounts(
            res.data.counts || {

              generalUnreadCount: 0,
              orderUnreadCount: 0,
              totalUnreadCount: 0

            }
          );

        } else {

          setNotifications([]);

          setGeneralNotifications([]);

          setOrderNotifications([]);

          setCounts({

            generalUnreadCount: 0,
            orderUnreadCount: 0,
            totalUnreadCount: 0

          });

        }

      } catch (err) {

        console.error(
          'Failed to fetch notifications:',
          err
        );

        setNotifications([]);

        setGeneralNotifications([]);

        setOrderNotifications([]);

      } finally {

        setLoading(false);

      }

    };

  // ✅ MARK SINGLE AS READ

  const markAsRead = async (id) => {

    try {

      await ApiService.post(
        '/notifications/markAllAsRead',
        {

          ids: [id],

          isRead: true,

          companyCode:
            initialAuthState.companyCode,

          unitCode:
            initialAuthState.unitCode

        }
      );

      // ✅ GENERAL

      setGeneralNotifications(prev =>

        prev.map(n =>

          n.id === id
            ? {
              ...n,
              isRead: 1
            }
            : n

        )

      );

      // ✅ ORDERS

      setOrderNotifications(prev =>

        prev.map(n =>

          n.id === id
            ? {
              ...n,
              isRead: 1
            }
            : n

        )

      );

      // ✅ UPDATE COUNTS

      setCounts(prev => {

        const totalUnreadCount =
          Math.max(
            prev.totalUnreadCount - 1,
            0
          );

        return {

          ...prev,

          totalUnreadCount

        };

      });

    } catch (err) {

      console.error(
        'Mark read failed:',
        err
      );

    }

  };

  // ✅ MARK ALL AS READ

  const markAllAsRead = async (
    ids = []
  ) => {

    try {

      if (!ids.length) return;

      await ApiService.post(
        '/notifications/markAllAsRead',
        {

          ids,

          isRead: true,

          companyCode:
            initialAuthState.companyCode,

          unitCode:
            initialAuthState.unitCode

        }
      );

      // ✅ GENERAL

      setGeneralNotifications(prev =>

        prev.map(n => ({
          ...n,
          isRead: 1
        }))

      );

      // ✅ ORDERS

      setOrderNotifications(prev =>

        prev.map(n => ({
          ...n,
          isRead: 1
        }))

      );

      // ✅ COUNTS RESET

      setCounts({

        generalUnreadCount: 0,
        orderUnreadCount: 0,
        totalUnreadCount: 0

      });

    } catch (err) {

      console.error(
        'Mark all failed:',
        err
      );

    }

  };

  // ✅ INITIAL FETCH

  useEffect(() => {

    fetchNotifications();

  }, []);

  // ✅ SOCKET

  useEffect(() => {

    socket.on("connect", () => {

      console.log(
        "✅ Socket Connected:",
        socket.id
      );

    });

    // socket.on(
    //   "newNotification",
    //   (data) => {

    //     console.log(
    //       "🔥 Notification Received:",
    //       data
    //     );

    //     // ✅ PLAY SOUND

    //     audioRef.current
    //       ?.play()
    //       .catch(err => {

    //         console.log(
    //           "Audio blocked:",
    //           err
    //         );

    //       });

    //     // ✅ REFRESH

    //     fetchNotifications();

    //   }
    // );


    socket.on(
      "newNotification",
      async (data) => {

        console.log(
          "🔥 Notification Received:",
          data
        );

        // ✅ REFRESH DATA

        await fetchNotifications();

        // ✅ PLAY SOUND ONLY FOR ORDERS

        if (
          data?.notificationType === "Order"
        ) {

          audioRef.current
            ?.play()
            .catch(err => {

              console.log(
                "Audio blocked:",
                err
              );

            });

        }

      }
    );
    return () => {

      socket.off(
        "newNotification"
      );

      socket.off(
        "connect"
      );

    };

  }, []);

  return (

    <NotificationContext.Provider
      value={{

        notifications,

        generalNotifications,

        orderNotifications,

        counts,

        loading,

        refreshNotifications:
          fetchNotifications,

        markAsRead,

        markAllAsRead

      }}
    >

      {children}

    </NotificationContext.Provider>

  );

};










// import React, { createContext, useContext, useEffect, useState } from 'react';
// import axios from 'axios';
// import ApiService, { initialAuthState } from '../services/ApiService';
// import socket from '../socket';

// const NotificationContext = createContext({
//   requestCount: 0,
//   ticketCount: 0,
//   refreshNotifications: () => { },
// });

// export const useNotificationContext = () => useContext(NotificationContext);

// export const NotificationProvider = ({ children }) => {
//   const [notifications, setNotifications] = useState([]);
//   const [requestCount, setRequestCount] = useState(0);
//   const [ticketCount, setTicketCount] = useState(0);

//   const fetchNotifications = async () => {
//     const notifyStaffId = localStorage.getItem("id")
//     try {
//       const payload = {
//         companyCode: initialAuthState.companyCode,
//         unitCode: initialAuthState.unitCode,
//         notifyStaffId: notifyStaffId
//       };

//       const res = await ApiService.post('/notifications/getAllNotifications', payload);
//       const data = res.data.notifications;
//       if (res.status) {
//         setNotifications(res.data.notifications || []);

//         const requestUnread = data.filter(
//           (n) => {
//             console.log("rrr :", n)
//             return (!n.isRead && n.notificationType === 'Request')
//           }
//         );
//         const ticketUnread = data.filter(
//           (n) => !n.isRead && n.notificationType === 'Ticket'
//         );
//         console.log("requestUnread :", requestUnread)
//         console.log("ticketUnread :", ticketUnread)

//         setRequestCount(requestUnread.length);
//         setTicketCount(ticketUnread.length);

//       } else {
//         setNotifications([]);
//       }
//     } catch (err) {
//       console.error('Failed to fetch notifications:', err);
//       setNotifications([]);
//     }
//   };


//   // useEffect(() => {
//   //   fetchNotifications();
//   // }, []);

//   useEffect(() => {

//     fetchNotifications();

//     socket.on("connect", () => {
//       console.log("✅ Socket Connected:", socket.id);
//     });

//     socket.on("newNotification", (data) => {

//       console.log("🔥 Notification Received:", data);

//       // refresh counts instantly
//       fetchNotifications();

//       // optional sound
//       const audio = new Audio("/sounds/notification.mp3");

//       audio.play().catch((err) => {
//         console.log("Audio blocked:", err);
//       });

//     });

//     return () => {
//       socket.off("newNotification");
//       socket.off("connect");
//     };

//   }, []);

//   return (
//     <NotificationContext.Provider
//       value={{ requestCount, ticketCount, refreshNotifications: fetchNotifications }}
//     >
//       {children}
//     </NotificationContext.Provider>
//   );
// };
